import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Feature {
  icon: string;
  title: string;
  description: string;
  delay: string;
}

const features: Feature[] = [
  {
    icon: 'ri-shield-check-line',
    title: 'Military-Grade Encryption',
    description:
      'AES-256 encryption ensures all data transmitted through KROKO VPN is completely unreadable to third parties, ISPs, and malicious actors.',
    delay: 'delay-100',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Strict Zero-Log Policy',
    description:
      'We never collect, store, or share your browsing activity. Your online behavior is your business — and yours alone.',
    delay: 'delay-200',
  },
  {
    icon: 'ri-global-line',
    title: 'Global Server Network',
    description:
      'Connect to high-speed servers across multiple regions with minimal latency, giving you fast and reliable access wherever you are.',
    delay: 'delay-300',
  },
  {
    icon: 'ri-scan-2-line',
    title: 'Deep Device Scanning',
    description:
      'KROKO Cleaner performs a comprehensive scan of your iPhone\'s storage, identifying redundant files, app caches, and duplicate media.',
    delay: 'delay-100',
  },
  {
    icon: 'ri-image-2-line',
    title: 'Duplicate Photo Detection',
    description:
      'Advanced visual comparison algorithms identify near-duplicate and identical photos so you can reclaim valuable storage in seconds.',
    delay: 'delay-200',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Real-Time Performance Insights',
    description:
      'Monitor your device health, storage usage, and cleanup history with an intuitive dashboard that keeps you in full control.',
    delay: 'delay-300',
  },
  {
    icon: 'ri-lock-password-line',
    title: 'DNS Leak Protection',
    description:
      'Our VPN routes all DNS queries through our encrypted tunnel, eliminating the risk of your queries being exposed to your network provider.',
    delay: 'delay-100',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'One-Tap Optimization',
    description:
      'A single tap is all it takes to analyze your device and clear junk files — no technical knowledge required.',
    delay: 'delay-200',
  },
  {
    icon: 'ri-apple-line',
    title: 'Native iOS Integration',
    description:
      'Both apps are built with native Swift frameworks to ensure perfect compatibility, fluid animations, and maximum battery efficiency.',
    delay: 'delay-300',
  },
];

function FeatureCard({ icon, title, description, delay }: Feature) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`animate-fade-up ${delay} ${isVisible ? 'visible' : ''} p-7 rounded-2xl bg-white border border-stone-100 hover:border-[#C74B1C]/30 transition-colors duration-300 cursor-default`}
    >
      <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#FDF1EC] mb-5">
        <i className={`${icon} text-xl text-[#C74B1C]`} />
      </div>
      <h4
        className="font-bold text-[#0D0D0D] mb-3 text-base"
        style={{ fontFamily: 'Syne, sans-serif' }}
      >
        {title}
      </h4>
      <p className="text-[#6B6B6B] text-sm leading-relaxed">{description}</p>
    </div>
  );
}

export default function FeaturesSection() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();

  return (
    <section id="features" className="py-28 bg-[#F5F3EE]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={titleRef}
          className={`text-center mb-16 animate-fade-up ${titleVisible ? 'visible' : ''}`}
        >
          <span className="text-[#C74B1C] text-sm font-semibold tracking-[0.2em] uppercase">
            Key Features
          </span>
          <h2
            className="mt-3 font-extrabold text-[#0D0D0D]"
            style={{ fontFamily: 'Syne, sans-serif', fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            Everything You Need,
            <br />
            Nothing You Don&apos;t
          </h2>
          <p className="mt-4 text-[#6B6B6B] text-base max-w-xl mx-auto leading-relaxed">
            Our products are precision-engineered to deliver the features that matter
            most — without bloat, complexity, or compromise.
          </p>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
}
